import { useEffect, useState } from 'react'
import { Globe, Loader2, RotateCcw, Search, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { api, getApiErrorMessage } from '@/lib/api'
import { getAdminRole, canAccess, type AdminRole } from '@/lib/auth'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface Domain {
  id: string
  name: string
  status: string
  account_email: string | null
  created_at: string
}

export function Domains() {
  const [domains, setDomains] = useState<Domain[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [role, setRole] = useState<AdminRole | null>(null)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const LIMIT = 50

  useEffect(() => {
    setRole(getAdminRole())
  }, [])

  const canUnsuspend = canAccess(role, 'abuse_analyst')

  const fetchDomains = async (newOffset = offset) => {
    setLoading(true)
    try {
      const res = await api.get('/admin/domains', {
        params: { search: search || undefined, limit: LIMIT, offset: newOffset },
      })
      setDomains(res.data.domains || [])
      setTotal(res.data.total || 0)
      setOffset(newOffset)
    } catch {
      toast.error('Failed to load domains')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDomains(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUnsuspend = async (domain: Domain) => {
    setActionLoading(domain.id)
    try {
      await api.post(`/admin/domains/${domain.id}/unsuspend`)
      toast.success(`Domain ${domain.name} unsuspended`)
      fetchDomains(offset)
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to unsuspend domain'))
    } finally {
      setActionLoading(null)
    }
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">active</Badge>
      case 'suspended':
        return <Badge variant="destructive">suspended</Badge>
      case 'pending':
        return <Badge variant="secondary">pending</Badge>
      case 'verification_expired':
        return <Badge variant="outline">expired</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          Domains
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Manage platform-wide domain status</p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by domain name..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchDomains(0)}
          />
        </div>
        <Button variant="outline" onClick={() => fetchDomains(0)} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Domain</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Account</th>
                  <th className="px-4 py-3 text-left font-medium">Created</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : domains.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No domains found
                    </td>
                  </tr>
                ) : (
                  domains.map((domain) => (
                    <tr key={domain.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{domain.name}</td>
                      <td className="px-4 py-3">{statusBadge(domain.status)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{domain.account_email || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(domain.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {domain.status === 'suspended' && canUnsuspend && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Unsuspend domain"
                            onClick={() => handleUnsuspend(domain)}
                            disabled={actionLoading === domain.id}
                          >
                            {actionLoading === domain.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <RotateCcw className="h-4 w-4 text-emerald-500" />
                            )}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={fetchDomains} />
        </CardContent>
      </Card>

      <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-500">
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Domains are auto-suspended when bounce rate exceeds 15% in 24 hours or a spam complaint is received.
            Unsuspending should only be done after investigating the root cause.
          </p>
        </div>
      </div>
    </div>
  )
}
